import { useIntl } from 'react-intl';
import { Typography } from '@eventbrite/marmalade';
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './TopDestinationCard.module.scss';

type TopDestinationCardProps = {
    displayName: string;
    imageUrl: string;
    locationSlug: string;
    languageSubdirectory: string;
    imageLoading: 'lazy' | 'eager';
};

export const TopDestinationCard = (props: TopDestinationCardProps) => {
    const {
        displayName,
        imageUrl,
        locationSlug,
        languageSubdirectory,
        imageLoading = 'lazy',
    } = props;

    const intl = useIntl();
    const imageRef = useRef<HTMLImageElement>(null);
    const [imageFailed, setImageFailed] = useState(false);

    // onError won't re-fire for images that already 404'd before hydration,
    // so also check the natural size once mounted.
    useEffect(() => {
        const image = imageRef.current;
        if (image && image.complete && image.naturalWidth === 0) {
            setImageFailed(true);
        }
    }, []);

    let linkHref = `/d/${locationSlug}/events/`;
    linkHref = languageSubdirectory ? '/' + languageSubdirectory + linkHref : linkHref;

    return (
        <div className={styles.root} data-testid="top-destination-card">
            <a
                href={linkHref}
                aria-labelledby={intl.formatMessage(
                    {
                        id: 'Browse events for {displayName}',
                        defaultMessage: 'Browse events for {displayName}',
                    },
                    { displayName },
                )}
                color="neutral-100"
            >
                {/* Branded fallback shown when the destination image is missing/broken. */}
                <div className={styles.fallback} aria-hidden="true" />
                <img
                    ref={imageRef}
                    className={classNames(styles.image, {
                        [styles.imageHidden]: imageFailed,
                    })}
                    src={imageUrl}
                    loading={imageLoading}
                    onError={() => setImageFailed(true)}
                    alt={intl.formatMessage(
                        {
                            id: 'An image of {displayName} super imposed with the text "{displayName}"',
                            defaultMessage:
                                'An image of {displayName} super imposed with the text "{displayName}"',
                        },
                        { displayName },
                    )}
                />
                <div className={styles.overlay}>
                    <Typography
                        className={styles.destinationName}
                        color="neutral-100"
                        variant="heading-md"
                        align="left"
                    >
                        {displayName}
                    </Typography>
                </div>
                <div className={styles.gradient} />
                <div className={styles.orangeBar} />
            </a>
        </div>
    );
};
